import type {
  ApolloCache,
  DefaultContext,
  MutationOptions,
  OperationVariables,
  QueryOptions
} from "@apollo/client"
import { GraphQLError, type GraphQLFormattedError } from "graphql"

import { ApiFormattedResponse } from "@models/api_formatted_response"
import { showErrorToast } from "@utils/toast"
import { client } from "index"

let invalidTokenShowToast = false

function isInvalidTokenError(
  errors?: ReadonlyArray<GraphQLFormattedError>
): boolean {
  return (
    errors?.some(
      (err) =>
        (err.extensions as { code?: string })?.code === "401" ||
        (err as { code?: string })?.code === "401"
    ) || false
  )
}

export async function mutate<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
  TContext extends Record<string, unknown> = DefaultContext,
  TCache extends ApolloCache<unknown> = ApolloCache<unknown>
>(
  options: MutationOptions<TData, TVariables, TContext, TCache>
): Promise<ApiFormattedResponse<TData>> {
  try {
    const response = await client.mutate<TData, TVariables, TContext, TCache>(
      //@ts-expect-error bbb
      options
    )

    if (isInvalidTokenError(response.errors) && invalidTokenShowToast) {
      return {
        isSuccess: false,
        errorMessage: response.errors![0].message
      }
    }

    if (Array.isArray(response?.errors) && response?.errors?.length > 0) {
      const errorMessage = (response.errors[0] as GraphQLError).message

      //* show toast once, dispatch event on first invalid token
      if (!invalidTokenShowToast) {
        showErrorToast(errorMessage)
        if (isInvalidTokenError(response.errors)) {
          window.dispatchEvent(new Event("invalidToken"))
          invalidTokenShowToast = true
        }
      }
      return {
        isSuccess: false,
        errorMessage: errorMessage
      }
    }
    return {
      isSuccess: true,
      data: response?.data ? response?.data : undefined
    }
  } catch (err) {
    return {
      isSuccess: false,
      errorMessage: (err as GraphQLError).message
    }
  }
}

export async function query<
  T = unknown,
  TVariables extends OperationVariables = OperationVariables
>(options: QueryOptions<TVariables, T>): Promise<ApiFormattedResponse<T>> {
  try {
    const response = await client.query<T, TVariables>(options)

    if (isInvalidTokenError(response.errors) && invalidTokenShowToast) {
      return {
        isSuccess: false,
        errorMessage: response.errors![0].message
      }
    }

    if (Array.isArray(response?.errors) && response?.errors?.length > 0) {
      const errorMessage = (response.errors[0] as GraphQLError).message

      //* show toast once, dispatch event on first invalid token
      if (!invalidTokenShowToast) {
        showErrorToast(errorMessage)
        if (isInvalidTokenError(response.errors)) {
          window.dispatchEvent(new Event("invalidToken"))
          invalidTokenShowToast = true
        }
      }

      return {
        isSuccess: false,
        errorMessage: errorMessage
      }
    }
    return {
      isSuccess: true,
      data: response?.data ? response?.data : undefined
    }
  } catch (err) {
    return {
      isSuccess: false,
      errorMessage: (err as GraphQLError).message
    }
  }
}
